import { memo, useEffect, useMemo, useRef, useState } from 'react';
import './MeshtasticWeatherHistoryChart.css';
import { getObservationDisplayTimestampMs } from './meshtasticWeatherTimestamps';
import {
  convertHistoryMetricValue,
  getHistoryMetricSummary,
  getHistoryResolutionLabel,
  historyTimeDomain,
  nearestHistoryPointIndex,
} from './meshtasticWeatherHistory';
import {
  convertPrecipitationValue,
  convertPressureValue,
  convertTemperatureValue,
  convertWindSpeedValue,
  formatUnitValue,
  getPressureDecimals,
  getPressureSuffix,
  getPrecipitationDecimals,
  getPrecipitationSuffix,
  getTemperatureSuffix,
  getWindSpeedSuffix,
  toNumber,
} from './meshtasticWeatherUnits';

const DEFAULT_VIEWBOX_WIDTH = 900;
const VIEWBOX_HEIGHT = 320;
const CHART_PADDING = {
  top: 14,
  right: 4,
  bottom: 44,
  left: 24,
};
const MIN_LEFT_GUTTER = 30;
const MAX_LEFT_GUTTER = 56;
const BATTERY_VOLTAGE_COLORS = {
  critical: '#8e3a2b',
  powerSave: '#b35a33',
  low: '#c9803a',
  normal: '#798e25',
  strongSun: '#10351f',
  unknown: '#8b9887',
};
const HISTORY_TABS = [
  {
    key: 'temperature',
    label: 'Temperature',
    color: '#c9803a',
    decimals: 1,
    suffix: ' C',
    description: 'Temperature trend across the selected time window.',
    valueKeys: ['temp_c', 'air_temp_c'],
    kind: 'temperature',
    icon: 'temperature',
  },
  {
    key: 'wind_speed',
    label: 'Wind',
    color: '#5d99bf',
    decimals: 1,
    suffix: ' m/s',
    description: 'Average wind speed across the selected time window.',
    valueKeys: ['wind_ms', 'wind_avg_ms'],
    kind: 'wind',
    icon: 'wind',
  },
  {
    key: 'humidity',
    label: 'Humidity',
    color: '#4f7c6b',
    decimals: 0,
    suffix: '%',
    description: 'Relative humidity trend across the selected time window.',
    valueKeys: ['relative_humidity_pct', 'humidity'],
    icon: 'humidity',
  },
  {
    key: 'pressure',
    label: 'Pressure',
    color: '#6d7f91',
    decimals: 1,
    suffix: ' hPa',
    description: 'Station pressure trend across the selected time window.',
    valueKeys: ['station_pressure_hpa', 'pressure_hpa', 'barometer_hpa'],
    kind: 'pressure',
    icon: 'pressure',
  },
  {
    key: 'rain',
    label: 'Rain',
    color: '#798e25',
    decimals: 1,
    suffix: ' mm',
    description: 'Rain accumulation per reported interval across the selected time window.',
    valueKeys: ['rain_interval_mm', 'rain_mm'],
    kind: 'precipitation',
    icon: 'rain',
  },
  {
    key: 'battery_voltage',
    label: 'Battery',
    color: '#43513f',
    decimals: 2,
    suffix: ' V',
    description: 'Weather station battery voltage across the selected time window. Normal range is 2.55V to 2.7V, with power saving below 2.45V and critical below 2.35V.',
    valueKeys: ['battery_voltage_v'],
    icon: 'battery',
  },
];

function HistoryMetricIcon({ name }) {
  return (
    <span className="mwh-tab__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        {name === 'temperature' && (
          <>
            <path d="M10 14.2V5.5a2 2 0 0 1 4 0v8.7" />
            <path d="M8.2 17.2a3.8 3.8 0 1 0 7.6 0c0-1.3-.7-2.4-1.8-3.1" />
          </>
        )}
        {name === 'wind' && (
          <>
            <path d="M3.5 8.5h10.8c2.1 0 3.2-1.1 3.2-2.5s-1.1-2.5-2.6-2.5c-1.2 0-2.2.7-2.5 1.8" />
            <path d="M3.5 13h14.8c1.9 0 3.2 1.1 3.2 2.7s-1.3 2.8-3 2.8c-1.4 0-2.4-.7-2.8-1.9" />
            <path d="M3.5 17h7" />
          </>
        )}
        {name === 'humidity' && (
          <path d="M12 21a6.2 6.2 0 0 0 6.2-6.2C18.2 10.1 12 3 12 3s-6.2 7.1-6.2 11.8A6.2 6.2 0 0 0 12 21Z" />
        )}
        {name === 'pressure' && (
          <>
            <path d="M5 16a7 7 0 1 1 14 0" />
            <path d="m12 16 3.2-5.4" />
            <path d="M8.4 16h7.2" />
          </>
        )}
        {name === 'rain' && (
          <>
            <path d="M4 12a8 8 0 0 1 16 0Z" />
            <path d="M4 12c1.6 0 1.6-1.5 3.2-1.5S8.8 12 10.4 12s1.6-1.5 3.2-1.5 1.6 1.5 3.2 1.5S18.4 10.5 20 12" />
            <path d="M12 12v5.3a2.7 2.7 0 0 0 5.4 0" />
          </>
        )}
        {name === 'battery' && (
          <>
            <rect x="3.5" y="7.5" width="15" height="9" rx="1.6" />
            <path d="M20.5 10.2v3.6" />
            <path d="M7 10.5v3" />
          </>
        )}
      </svg>
    </span>
  );
}

function HistoryExportIcon() {
  return (
    <span className="mwh-export-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M12 3.8v10" />
        <path d="m7.4 9.7 4.6 4.6 4.6-4.6" />
        <path d="M5 17.5v1.7c0 .6.5 1 1 1h12c.6 0 1-.5 1-1v-1.7" />
      </svg>
    </span>
  );
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function formatDateTimeLocalValue(timestampMs) {
  if (!Number.isFinite(timestampMs)) {
    return '';
  }

  const date = new Date(timestampMs);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const localTimestampMs = date.getTime() - (date.getTimezoneOffset() * 60 * 1000);
  return new Date(localTimestampMs).toISOString().slice(0, 16);
}

function parseDateTimeLocalValue(value) {
  const timestampMs = Date.parse(value);
  return Number.isFinite(timestampMs) ? timestampMs : null;
}

function getZoomSelectionRect(selection, points, plotHeight) {
  if (!selection || points.length < 2) {
    return null;
  }

  const startPoint = points[Math.min(selection.startIndex, selection.endIndex)];
  const endPoint = points[Math.max(selection.startIndex, selection.endIndex)];

  if (!startPoint || !endPoint) {
    return null;
  }

  return {
    x: startPoint.x,
    width: Math.max(endPoint.x - startPoint.x, 4),
    y: CHART_PADDING.top,
    height: plotHeight,
  };
}

function degreesToCardinal(value) {
  const numeric = toNumber(value);

  if (numeric === null) {
    return 'Variable';
  }

  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const normalized = ((numeric % 360) + 360) % 360;
  return directions[Math.round(normalized / 45) % directions.length];
}

function formatMetricValue(
  value,
  metric,
  temperatureUnit = 'c',
  windSpeedUnit = 'ms',
  precipitationUnit = 'mm',
  pressureUnit = 'hpa',
) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'n/a';
  }

  if (metric.kind === 'temperature') {
    return formatUnitValue(value, getTemperatureSuffix(temperatureUnit), metric.decimals);
  }

  if (metric.kind === 'wind') {
    return formatUnitValue(value, getWindSpeedSuffix(windSpeedUnit), metric.decimals);
  }

  if (metric.kind === 'precipitation') {
    return formatUnitValue(
      value,
      getPrecipitationSuffix(precipitationUnit),
      getPrecipitationDecimals(precipitationUnit, metric.decimals),
    );
  }

  if (metric.kind === 'pressure') {
    return formatUnitValue(
      value,
      getPressureSuffix(pressureUnit),
      getPressureDecimals(pressureUnit, metric.decimals),
    );
  }

  return `${Number(value).toFixed(metric.decimals)}${metric.suffix || ''}`;
}

function formatAxisTick(value, decimals = 2) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return numericValue.toFixed(decimals).replace(/\.?0+$/, '');
}

function getRainAxisMax(values) {
  const numericValues = (values || []).filter((value) => typeof value === 'number' && value >= 0);
  const maxValue = numericValues.length ? Math.max(...numericValues) : 0;

  if (maxValue <= 0) {
    return 1;
  }

  return maxValue < 1
    ? maxValue * 1.2
    : maxValue + (maxValue * 0.15);
}

function getBatteryVoltageLevel(value) {
  const numericValue = toNumber(value);

  if (numericValue === null) {
    return 'unknown';
  }

  if (numericValue < 2.35) {
    return 'critical';
  }

  if (numericValue < 2.45) {
    return 'powerSave';
  }

  if (numericValue < 2.55) {
    return 'low';
  }

  if (numericValue >= 2.6) {
    return 'strongSun';
  }

  return 'normal';
}

function getBatteryVoltageColor(value) {
  return BATTERY_VOLTAGE_COLORS[getBatteryVoltageLevel(value)] || BATTERY_VOLTAGE_COLORS.unknown;
}

function getMetricPointColor(metric, value) {
  if (metric?.key === 'battery_voltage') {
    return getBatteryVoltageColor(value);
  }

  return metric?.color;
}

export function buildColoredLineSegments(points, metric) {
  return points.slice(1).flatMap((point, index) => {
    const previousPoint = points[index];
    if (typeof point.value !== 'number' || typeof previousPoint.value !== 'number' || point.gapBefore) return [];
    const referenceValue = metric?.key === 'battery_voltage'
      ? (previousPoint.value + point.value) / 2
      : point.value;

    return {
      key: `${previousPoint.observedAtMs}-${point.observedAtMs}`,
      color: getMetricPointColor(metric, referenceValue),
      path: `M ${previousPoint.x.toFixed(2)} ${previousPoint.y.toFixed(2)} L ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
    };
  });
}

function getObservationDate(item) {
  const timestampMs = getObservationDisplayTimestampMs(item);

  if (!timestampMs) {
    return null;
  }

  return new Date(timestampMs);
}

function getMetricValue(
  weather,
  metric,
  temperatureUnit = 'c',
  windSpeedUnit = 'ms',
  pressureUnit = 'hpa',
) {
  const rawValue = metric.valueKeys.reduce((matchedValue, key) => {
    if (matchedValue !== undefined && matchedValue !== null && matchedValue !== '') {
      return matchedValue;
    }

    return weather?.[key];
  }, undefined);

  if (rawValue === null || rawValue === undefined || rawValue === '') return null;

  if (metric.kind === 'temperature') {
    return convertTemperatureValue(rawValue, temperatureUnit);
  }

  if (metric.kind === 'wind') {
    return convertWindSpeedValue(rawValue, windSpeedUnit);
  }

  if (metric.kind === 'pressure') {
    return convertPressureValue(rawValue, pressureUnit);
  }

  return toNumber(rawValue);
}

function getRainIntervalValue(weather, precipitationUnit = 'mm') {
  const reportedInterval = toNumber(weather?.rain_interval_mm ?? weather?.rain_mm);

  if (reportedInterval !== null) {
    return convertPrecipitationValue(Math.max(reportedInterval, 0), precipitationUnit);
  }

  return null;
}

export function getRainTotalStatValue(intervalTotal, rainTotalOverride = null) {
  if (typeof rainTotalOverride === 'number' && Number.isFinite(rainTotalOverride)) {
    return rainTotalOverride;
  }

  const numericIntervalTotal = typeof intervalTotal === 'number' && Number.isFinite(intervalTotal)
    ? intervalTotal
    : null;

  return numericIntervalTotal;
}

export function normalizeHistoryItems(
  items,
  temperatureUnit = 'c',
  windSpeedUnit = 'ms',
  precipitationUnit = 'mm',
  pressureUnit = 'hpa',
) {
  const sortedItems = (items || [])
    .map((item) => {
      const weather = item?.weather || item?.raw_payload?.payload?.weather || {};
      const observedAt = getObservationDate(item);

      if (!observedAt) {
        return null;
      }

      return {
        bucketStartMs: Date.parse(item.bucketStart),
        bucketEndMs: Date.parse(item.bucketEnd),
        count: toNumber(item.count),
        stats: item.stats,
        observedAtMs: observedAt.getTime(),
        observedAtLabel: observedAt.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        }),
        observedAtDateLabel: observedAt.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
        }),
        observedAtFullLabel: observedAt.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
        weather,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.observedAtMs - right.observedAtMs);

  return sortedItems.map((item) => {
    const metricValues = HISTORY_TABS.reduce((accumulator, metric) => {
      accumulator[metric.key] = metric.kind === 'precipitation'
        ? getRainIntervalValue(item.weather, precipitationUnit)
        : getMetricValue(item.weather, metric, temperatureUnit, windSpeedUnit, pressureUnit);
      return accumulator;
    }, {});

    return {
      bucketStartMs: item.bucketStartMs,
      bucketEndMs: item.bucketEndMs,
      count: item.count,
      stats: item.stats,
      observedAtMs: item.observedAtMs,
      observedAtLabel: item.observedAtLabel,
      observedAtDateLabel: item.observedAtDateLabel,
      observedAtFullLabel: item.observedAtFullLabel,
      wind_dir_deg: item.weather.wind_dir_deg == null ? null : toNumber(item.weather.wind_dir_deg),
      ...metricValues,
    };
  });
}

function getAxisTicks(minValue, maxValue, count = 4) {
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return [];
  }

  if (Math.abs(maxValue - minValue) < 0.0001) {
    return [minValue];
  }

  const step = (maxValue - minValue) / count;

  return Array.from({ length: count + 1 }, (_, index) => minValue + (step * index));
}

function getTooltipAlignment(xPercent, yPercent) {
  if (xPercent < 18) {
    return yPercent < 25 ? 'right-below' : 'right-above';
  }

  if (xPercent > 82) {
    return yPercent < 25 ? 'left-below' : 'left-above';
  }

  return yPercent < 25 ? 'center-below' : 'center-above';
}

function WeatherChartTooltip({
  tooltip,
  metric,
  temperatureUnit,
  windSpeedUnit,
  precipitationUnit,
  pressureUnit,
}) {
  if (!tooltip) {
    return null;
  }

  const point = tooltip.point || tooltip;
  const xPercent = Number.isFinite(tooltip.xPercent) ? tooltip.xPercent : point.xPercent;
  const yPercent = Number.isFinite(tooltip.yPercent) ? tooltip.yPercent : point.yPercent;

  if (!point || !point.observedAtFullLabel) {
    return null;
  }

  return (
    <div
      className={`mwh-tooltip mwh-tooltip--${getTooltipAlignment(xPercent, yPercent)}`}
      style={{ left: `${xPercent}%`, top: `${yPercent}%` }}
      aria-hidden="true"
    >
      <p className="mwh-tooltip__time">{point.observedAtFullLabel}</p>
      {Number.isFinite(point.bucketEndMs) && (
        <p className="mwh-tooltip__detail">
          Until {new Date(point.bucketEndMs).toLocaleString()} · {point.count?.toLocaleString() ?? 'Unknown'} readings
        </p>
      )}
      <p className="mwh-tooltip__value">
        {formatMetricValue(
          point[metric.key],
          metric,
          temperatureUnit,
          windSpeedUnit,
          precipitationUnit,
          pressureUnit,
        )}
      </p>
      {Number.isFinite(point.bucketEndMs) && (
        <p className="mwh-tooltip__detail">
          {metric.kind === 'precipitation' ? 'Interval total' : 'Interval average'}
          {metric.key === 'temperature' && Number.isFinite(point.rangeMin) && Number.isFinite(point.rangeMax)
            ? ` · ${formatMetricValue(point.rangeMin, metric, temperatureUnit, windSpeedUnit, precipitationUnit, pressureUnit)} to ${formatMetricValue(point.rangeMax, metric, temperatureUnit, windSpeedUnit, precipitationUnit, pressureUnit)}`
            : ''}
        </p>
      )}
      {metric.key === 'wind_speed' && point.wind_dir_deg !== null && (
        <p className="mwh-tooltip__detail">
          Direction: {Math.round(point.wind_dir_deg)} deg ({degreesToCardinal(point.wind_dir_deg)})
        </p>
      )}
    </div>
  );
}

function WindDirectionMarker({ point, index, markerStep, totalPoints }) {
  if (index % markerStep !== 0 && index !== totalPoints - 1) {
    return null;
  }

  if (point.wind_dir_deg === null) {
    return null;
  }

  return (
    <g transform={`translate(${point.x}, ${point.y}) rotate(${point.wind_dir_deg})`}>
      <circle className="mwh-wind-marker__bg" r="12.5" />
      <path
        className="mwh-wind-marker__arrow"
        d="M0 -11.2 L6.4 0.8 L2.4 0.8 L2.4 9.4 L-2.4 9.4 L-2.4 0.8 L-6.4 0.8 Z"
      />
    </g>
  );
}

function MeshtasticWeatherHistoryChart({
  historyItems,
  chartWidth,
  historyMeta,
  historySummary,
  loading,
  error,
  selectedWindowKey,
  selectedWindow,
  temperatureUnit,
  windSpeedUnit,
  precipitationUnit,
  pressureUnit,
  windowOptions,
  customWindowRange,
  canExportCsv = false,
  csvExportError = '',
  csvExporting = false,
  csvExportMessage = '',
  rainTotalLoading = false,
  rainTotalOverride = null,
  onWindowChange,
  onCustomWindowApply,
  onZoomRangeSelect,
  onExportCsv,
  onExportChartCsv,
  csvDownloadUrl,
  csvFilename,
}) {
  const viewBoxWidth = Math.min(DEFAULT_VIEWBOX_WIDTH, Math.max(320, (chartWidth || DEFAULT_VIEWBOX_WIDTH + 30) - 30));
  const [activeMetricKey, setActiveMetricKey] = useState(HISTORY_TABS[0].key);
  const [tooltip, setTooltip] = useState(null);
  const [customFromInput, setCustomFromInput] = useState('');
  const [customToInput, setCustomToInput] = useState('');
  const [customRangeError, setCustomRangeError] = useState('');
  const [zoomSelection, setZoomSelection] = useState(null);
  const hoveredPointIndexRef = useRef(-1);
  const pendingHoverIndexRef = useRef(-1);
  const pendingTooltipPointRef = useRef(null);
  const hoverFrameRef = useRef(null);
  const zoomDragRef = useRef(null);
  const chartData = useMemo(() => normalizeHistoryItems(
    historyItems,
    temperatureUnit,
    windSpeedUnit,
    precipitationUnit,
    pressureUnit,
  ), [historyItems, pressureUnit, precipitationUnit, temperatureUnit, windSpeedUnit]);
  const activeMetric = useMemo(
    () => HISTORY_TABS.find((metric) => metric.key === activeMetricKey) || HISTORY_TABS[0],
    [activeMetricKey],
  );
  const selectedWindowDetails = useMemo(
    () => (
      selectedWindow ||
      windowOptions.find((option) => option.key === selectedWindowKey) ||
      windowOptions[0]
    ),
    [selectedWindow, selectedWindowKey, windowOptions],
  );
  const metricValues = useMemo(
    () => chartData
      .map((point) => point[activeMetric.key])
      .filter((value) => typeof value === 'number'),
    [activeMetric, chartData],
  );
  const metricStats = useMemo(() => {
    if (!metricValues.length) {
      return {
        latestValue: null,
        averageValue: null,
        minValue: null,
        maxValue: null,
        totalValue: null,
      };
    }

    return metricValues.reduce((stats, value, index) => ({
      latestValue: index === metricValues.length - 1 ? value : stats.latestValue,
      minValue: stats.minValue === null ? value : Math.min(stats.minValue, value),
      maxValue: stats.maxValue === null ? value : Math.max(stats.maxValue, value),
      totalValue: stats.totalValue + value,
    }), {
      latestValue: metricValues[metricValues.length - 1],
      averageValue: null,
      minValue: null,
      maxValue: null,
      totalValue: 0,
    });
  }, [metricValues]);
  const {
    latestValue,
    averageValue,
    minValue,
    maxValue,
    totalValue,
  } = useMemo(() => {
    if (historySummary || historyMeta?.mode === 'aggregate') {
      return getHistoryMetricSummary(historySummary, activeMetric, {
        temperatureUnit, windSpeedUnit, precipitationUnit, pressureUnit,
      });
    }
    return {
      ...metricStats,
      averageValue: metricValues.length ? metricStats.totalValue / metricValues.length : null,
    };
  }, [activeMetric, historyMeta?.mode, historySummary, metricStats, metricValues.length,
    temperatureUnit, windSpeedUnit, precipitationUnit, pressureUnit]);
  const isRainMetric = activeMetric.kind === 'precipitation';
  const rainTotalStatValue = useMemo(() => (
    isRainMetric && !rainTotalLoading
      ? getRainTotalStatValue(totalValue, rainTotalOverride)
      : totalValue
  ), [isRainMetric, rainTotalLoading, rainTotalOverride, totalValue]);
  const subtitle = 'Choose a metric, pick a time window, or drag across the graph to zoom into a specific period.';
  const axisTickDecimals = useMemo(() => {
    if (activeMetric.kind === 'precipitation') {
      return getPrecipitationDecimals(precipitationUnit, activeMetric.decimals);
    }

    if (activeMetric.kind === 'pressure') {
      return getPressureDecimals(pressureUnit, activeMetric.decimals);
    }

    return activeMetric.decimals ?? 1;
  }, [activeMetric, precipitationUnit, pressureUnit]);
  const xAxisDataKey = selectedWindowDetails.hours > 24 ? 'observedAtDateLabel' : 'observedAtLabel';
  const statItems = useMemo(() => (
    isRainMetric
      ? [
        {
          label: 'Total',
          loading: rainTotalLoading,
          value: rainTotalLoading
            ? 'Loading...'
            : formatMetricValue(
              rainTotalStatValue,
              activeMetric,
              temperatureUnit,
              windSpeedUnit,
              precipitationUnit,
            ),
        },
        {
          label: 'Avg',
          value: formatMetricValue(
            averageValue,
            activeMetric,
            temperatureUnit,
            windSpeedUnit,
            precipitationUnit,
            pressureUnit,
          ),
        },
        {
          label: 'Min',
          value: formatMetricValue(
            minValue,
            activeMetric,
            temperatureUnit,
            windSpeedUnit,
            precipitationUnit,
            pressureUnit,
          ),
        },
        {
          label: 'Max',
          value: formatMetricValue(
            maxValue,
            activeMetric,
            temperatureUnit,
            windSpeedUnit,
            precipitationUnit,
            pressureUnit,
          ),
        },
      ]
      : [
        {
          label: 'Latest',
          value: formatMetricValue(
            latestValue,
            activeMetric,
            temperatureUnit,
            windSpeedUnit,
            precipitationUnit,
            pressureUnit,
          ),
        },
        {
          label: 'Avg',
          value: formatMetricValue(
            averageValue,
            activeMetric,
            temperatureUnit,
            windSpeedUnit,
            precipitationUnit,
            pressureUnit,
          ),
        },
        {
          label: 'Min',
          value: formatMetricValue(
            minValue,
            activeMetric,
            temperatureUnit,
            windSpeedUnit,
            precipitationUnit,
            pressureUnit,
          ),
        },
        {
          label: 'Max',
          value: formatMetricValue(
            maxValue,
            activeMetric,
            temperatureUnit,
            windSpeedUnit,
            precipitationUnit,
            pressureUnit,
          ),
        }
      ]
  ), [
    activeMetric,
    averageValue,
    isRainMetric,
    latestValue,
    maxValue,
    minValue,
    pressureUnit,
    precipitationUnit,
    rainTotalLoading,
    rainTotalStatValue,
    temperatureUnit,
    windSpeedUnit,
  ]);

  const rangeLow = Math.min(...metricValues, ...(Number.isFinite(minValue) ? [minValue] : []));
  const rangeHigh = Math.max(...metricValues, ...(Number.isFinite(maxValue) ? [maxValue] : []));
  const yMin = isRainMetric ? 0 : Number.isFinite(rangeLow) ? rangeLow === rangeHigh ? rangeLow - 1 : rangeLow : 0;
  const yMax = isRainMetric ? getRainAxisMax(metricValues) : Number.isFinite(rangeHigh) ? rangeLow === rangeHigh ? rangeHigh + 1 : rangeHigh : 1;
  const axisTicks = useMemo(() => getAxisTicks(yMin, yMax, 4), [yMax, yMin]);
  const chartPaddingLeft = useMemo(() => {
    const maxLabelLength = Math.max(
      ...axisTicks.map((tick) => formatAxisTick(tick, axisTickDecimals).length),
      1,
    );

    return clamp((maxLabelLength * 8) + 10, MIN_LEFT_GUTTER, MAX_LEFT_GUTTER);
  }, [axisTickDecimals, axisTicks]);
  const plotWidth = viewBoxWidth - chartPaddingLeft - CHART_PADDING.right;
  const plotHeight = VIEWBOX_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;
  const timeDomain = useMemo(() => historyTimeDomain(chartData, historyMeta), [chartData, historyMeta]);
  const expectedIntervalMs = useMemo(() => {
    const intervals = chartData.slice(1).map((point, index) => point.observedAtMs - chartData[index].observedAtMs)
      .filter((value) => value > 0).sort((left, right) => left - right);
    return intervals.length ? intervals[Math.floor((intervals.length - 1) / 2)] : Infinity;
  }, [chartData]);
  const points = useMemo(() => chartData.map((point, index) => {
    const x = chartPaddingLeft + plotWidth * clamp((point.observedAtMs - timeDomain.from) / (timeDomain.to - timeDomain.from), 0, 1);
    const rawValue = point[activeMetric.key];
    const safeValue = typeof rawValue === 'number' ? rawValue : yMin;
    const yRatio = yMax === yMin ? 0.5 : (safeValue - yMin) / (yMax - yMin);
    const y = CHART_PADDING.top + ((1 - clamp(yRatio, 0, 1)) * plotHeight);
    const stats = activeMetric.valueKeys.map((key) => point.stats?.[key]).find(Boolean);
    const units = { temperatureUnit, windSpeedUnit, precipitationUnit, pressureUnit };
    const rangeMin = convertHistoryMetricValue(stats?.min, activeMetric, units);
    const rangeMax = convertHistoryMetricValue(stats?.max, activeMetric, units);
    const toY = (value) => CHART_PADDING.top + (1 - clamp((value - yMin) / (yMax - yMin || 1), 0, 1)) * plotHeight;
    const previous = chartData[index - 1];
    const gapBefore = Boolean(previous && (Number.isFinite(previous.bucketEndMs)
      ? point.observedAtMs > previous.bucketEndMs
      : point.observedAtMs - previous.observedAtMs > Math.min(expectedIntervalMs * 3, 30 * 60 * 1000)));

    return {
      ...point,
      x,
      y,
      label: point[xAxisDataKey],
      value: rawValue,
      gapBefore,
      rangeMin,
      rangeMax,
      rangeMinY: rangeMin === null ? null : toY(rangeMin),
      rangeMaxY: rangeMax === null ? null : toY(rangeMax),
      bucketWidth: Number.isFinite(point.bucketEndMs)
        ? plotWidth * (Math.min(point.bucketEndMs, timeDomain.to) - Math.max(point.observedAtMs, timeDomain.from)) / (timeDomain.to - timeDomain.from)
        : null,
      xPercent: (x / viewBoxWidth) * 100,
      yPercent: (y / VIEWBOX_HEIGHT) * 100,
    };
  }), [activeMetric, chartData, chartPaddingLeft, plotHeight, plotWidth, viewBoxWidth, timeDomain, expectedIntervalMs,
    temperatureUnit, windSpeedUnit, precipitationUnit, pressureUnit, xAxisDataKey, yMax, yMin]);
  const xTicks = useMemo(() => Array.from({ length: 6 }, (_, index) => {
    const observedAtMs = timeDomain.from + (timeDomain.to - timeDomain.from) * index / 5;
    const options = selectedWindowDetails.hours <= 24
      ? { hour: 'numeric', minute: '2-digit' }
      : { month: 'short', day: 'numeric', ...(selectedWindowDetails.hours > 24 * 365 ? { year: '2-digit' } : {}) };
    return { observedAtMs, x: chartPaddingLeft + plotWidth * index / 5, label: new Date(observedAtMs).toLocaleString([], options) };
  }), [timeDomain, selectedWindowDetails.hours, chartPaddingLeft, plotWidth]);
  const pointSpacing = points.length > 1 ? plotWidth / (points.length - 1) : plotWidth;
  const barWidth = Math.min(18, Math.max(1.25, pointSpacing * 0.62));
  const windMarkerStep = Math.max(1, Math.ceil(points.length / 16));
  const lineSegments = useMemo(
    () => buildColoredLineSegments(points, activeMetric),
    [activeMetric, points],
  );
  const activeTooltipPoint = tooltip?.point || tooltip || null;
  const zoomSelectionRect = getZoomSelectionRect(zoomSelection, points, plotHeight);

  useEffect(() => () => {
    if (hoverFrameRef.current !== null) {
      window.cancelAnimationFrame(hoverFrameRef.current);
    }
  }, []);

  useEffect(() => {
    setCustomFromInput(formatDateTimeLocalValue(customWindowRange?.fromMs));
    setCustomToInput(formatDateTimeLocalValue(customWindowRange?.toMs));
    setCustomRangeError('');
  }, [customWindowRange]);

  const updateTooltipForX = (chartX) => {
    if (!points.length || !Number.isFinite(chartX)) {
      return;
    }

    const clampedX = clamp(
      chartX,
      chartPaddingLeft,
      viewBoxWidth - CHART_PADDING.right,
    );
    const nearestIndex = nearestHistoryPointIndex(points, clampedX);

    if (hoveredPointIndexRef.current === nearestIndex) {
      return;
    }

    pendingHoverIndexRef.current = nearestIndex;
    pendingTooltipPointRef.current = points[nearestIndex];

    if (hoverFrameRef.current !== null) {
      return;
    }

    hoverFrameRef.current = window.requestAnimationFrame(() => {
      hoverFrameRef.current = null;

      if (hoveredPointIndexRef.current === pendingHoverIndexRef.current) {
        return;
      }

      hoveredPointIndexRef.current = pendingHoverIndexRef.current;
      setTooltip(pendingTooltipPointRef.current);
    });
  };

  const updateTooltipFromClientX = (target, clientX) => {
    const bounds = target.getBoundingClientRect();
    if (!bounds.width) {
      return;
    }

    const xRatio = (clientX - bounds.left) / bounds.width;
    updateTooltipForX(chartPaddingLeft + xRatio * plotWidth);
  };

  const getPointIndexFromClientX = (target, clientX) => {
    const bounds = target.getBoundingClientRect();

    if (!bounds.width || points.length < 2) {
      return -1;
    }

    const xRatio = (clientX - bounds.left) / bounds.width;
    const chartX = clamp(chartPaddingLeft + xRatio * plotWidth, chartPaddingLeft, viewBoxWidth - CHART_PADDING.right);

    return nearestHistoryPointIndex(points, chartX);
  };

  const handleChartMouseMove = (event) => {
    updateTooltipFromClientX(event.currentTarget, event.clientX);
  };

  const clearTooltip = () => {
    if (hoverFrameRef.current !== null) {
      window.cancelAnimationFrame(hoverFrameRef.current);
      hoverFrameRef.current = null;
    }

    pendingHoverIndexRef.current = -1;
    pendingTooltipPointRef.current = null;
    hoveredPointIndexRef.current = -1;
    setTooltip(null);
  };

  const submitCustomRange = (event) => {
    event.preventDefault();

    const fromMs = parseDateTimeLocalValue(customFromInput);
    const toMs = parseDateTimeLocalValue(customToInput);

    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
      setCustomRangeError('Enter a valid start and end time.');
      return;
    }

    if (toMs <= fromMs) {
      setCustomRangeError('End time must be after start time.');
      return;
    }

    setCustomRangeError('');
    onCustomWindowApply?.({ fromMs, toMs });
  };

  const handleWindowSelectChange = (event) => {
    setCustomRangeError('');
    onWindowChange(event.target.value);
  };

  const startZoomSelectionFromClientX = (target, clientX, metadata = {}) => {
    const pointIndex = getPointIndexFromClientX(target, clientX);

    if (pointIndex < 0) {
      return false;
    }

    zoomDragRef.current = {
      ...metadata,
      startIndex: pointIndex,
      endIndex: pointIndex,
    };
    setZoomSelection(zoomDragRef.current);
    updateTooltipFromClientX(target, clientX);
    return true;
  };

  const updateZoomSelectionFromClientX = (target, clientX) => {
    if (!zoomDragRef.current) {
      return false;
    }

    const pointIndex = getPointIndexFromClientX(target, clientX);

    if (pointIndex < 0) {
      return false;
    }

    if (pointIndex === zoomDragRef.current.endIndex) {
      updateTooltipFromClientX(target, clientX);
      return true;
    }

    zoomDragRef.current = {
      ...zoomDragRef.current,
      endIndex: pointIndex,
    };
    setZoomSelection(zoomDragRef.current);
    updateTooltipFromClientX(target, clientX);
    return true;
  };

  const finishZoomSelection = () => {
    const selection = zoomDragRef.current;

    if (!selection) {
      return;
    }

    zoomDragRef.current = null;
    setZoomSelection(null);

    if (selection.startIndex === selection.endIndex) {
      return;
    }

    const fromPoint = points[Math.min(selection.startIndex, selection.endIndex)];
    const toPoint = points[Math.max(selection.startIndex, selection.endIndex)];

    if (!fromPoint || !toPoint || toPoint.observedAtMs <= fromPoint.observedAtMs) {
      return;
    }

    onZoomRangeSelect?.({
      fromMs: Math.max(fromPoint.observedAtMs, timeDomain.from),
      toMs: Number.isFinite(toPoint.bucketEndMs) ? Math.min(toPoint.bucketEndMs, timeDomain.to) : toPoint.observedAtMs,
    });
  };

  const startZoomSelection = (event) => {
    if (event.pointerType === 'touch' || (event.button !== undefined && event.button !== 0)) {
      return;
    }

    const started = startZoomSelectionFromClientX(event.currentTarget, event.clientX, {
      pointerId: event.pointerId,
      pointerType: event.pointerType || 'mouse',
    });

    if (!started) {
      return;
    }

    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const updateZoomSelection = (event) => {
    if (event.pointerType === 'touch') {
      return;
    }

    if (updateZoomSelectionFromClientX(event.currentTarget, event.clientX)) {
      event.preventDefault();
    }
  };

  const finishPointerZoomSelection = (event) => {
    if (event.pointerType === 'touch') {
      return;
    }

    const pointerId = zoomDragRef.current?.pointerId;

    if (pointerId !== undefined) {
      event.currentTarget.releasePointerCapture?.(pointerId);
    }

    finishZoomSelection();
    event.preventDefault();
  };

  const cancelZoomSelection = () => {
    zoomDragRef.current = null;
    setZoomSelection(null);
  };

  return (
    <section
      className="weather-panel weather-history-panel"
      style={{ '--mwh-active-color': activeMetric.color }}
    >
      <div className="weather-panel__body">
        <div className="mwh-header">
          <div>
            <h3 className="weather-panel__title">Explore Historical Data</h3>
            <p className="mwh-subtitle">{subtitle}</p>
            <p className="mwh-resolution">Times shown in {Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time zone'}.</p>
            {historyMeta?.coverage?.availableFrom && (
              <p className="mwh-resolution">
                Available records: {new Date(historyMeta.coverage.availableFrom).toLocaleDateString()} – {new Date(historyMeta.coverage.availableTo || historyMeta.to).toLocaleDateString()}
              </p>
            )}
            {!error && historyItems.length > 0 && (
              <p className="mwh-resolution" role="status">
                {getHistoryResolutionLabel(historyMeta)} · {historyItems.length.toLocaleString()} {historyMeta?.mode === 'aggregate' ? 'intervals' : 'points'}
                {Number.isFinite(historyMeta?.rawCount) ? ` · ${historyMeta.rawCount.toLocaleString()} readings` : ''}
                {historyMeta?.mode === 'aggregate' ? ' · Temperature shading shows interval lows and highs. Zoom in for more detail.' : ''}
              </p>
            )}
          </div>
          <div className="mwh-controls">
            {loading && (
              <div className="mwh-loading-indicator" aria-live="polite">
                <span className="weather-spinner weather-spinner--small" />
                <span>Loading history...</span>
              </div>
            )}
            <label className="mwh-window-picker" htmlFor="mwh-window-select">
              <span className="mwh-window-picker__label">Time Window</span>
              <select
                id="mwh-window-select"
                className="mwh-window-select"
                value={selectedWindowKey}
                onChange={handleWindowSelectChange}
              >
                {windowOptions.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {selectedWindowKey === 'custom' && (
              <form className="mwh-custom-range" onSubmit={submitCustomRange}>
                <label className="mwh-custom-field" htmlFor="mwh-custom-from">
                  <span className="mwh-window-picker__label">From</span>
                  <input
                    id="mwh-custom-from"
                    className="mwh-custom-input"
                    type="datetime-local"
                    value={customFromInput}
                    onChange={(event) => setCustomFromInput(event.target.value)}
                  />
                </label>
                <label className="mwh-custom-field" htmlFor="mwh-custom-to">
                  <span className="mwh-window-picker__label">To</span>
                  <input
                    id="mwh-custom-to"
                    className="mwh-custom-input"
                    type="datetime-local"
                    value={customToInput}
                    onChange={(event) => setCustomToInput(event.target.value)}
                  />
                </label>
                <button className="mwh-custom-apply" type="submit">
                  Apply
                </button>
                {customRangeError && (
                  <p className="mwh-custom-error" role="alert">{customRangeError}</p>
                )}
              </form>
            )}
            <button
              className="mwh-export-button"
              type="button"
              onClick={onExportChartCsv}
              disabled={!historyItems.length || loading || Boolean(error)}
              title="Download the displayed readings or interval summaries in standard metric units"
            >
              <HistoryExportIcon />
              <span>Download chart data</span>
            </button>
            <button
              className="mwh-export-button"
              type="button"
              onClick={onExportCsv}
              disabled={!canExportCsv || csvExporting}
              aria-busy={csvExporting}
              title="Prepare all original readings for this range as a CSV file, in standard metric units and UTC"
            >
              <HistoryExportIcon />
              <span>{csvExporting ? 'Preparing export...' : 'Download all readings'}</span>
            </button>
            {csvDownloadUrl && (
              <a className="mwh-export-button" href={csvDownloadUrl} download={csvFilename || undefined}>
                Download prepared CSV
              </a>
            )}
            {(csvExportError || csvExportMessage) && (
              <p
                className={`mwh-export-note ${csvExportError ? 'mwh-export-note--error' : ''}`.trim()}
                role={csvExportError ? 'alert' : 'status'}
              >
                {csvExportError || csvExportMessage}
              </p>
            )}
          </div>
          <div className="mwh-stats">
            {statItems.map((item) => (
              <div key={item.label} className={`mwh-stat ${item.loading ? 'mwh-stat--loading' : ''}`}>
                <span className="mwh-stat__label">{item.label}</span>
                <span className="mwh-stat__value">
                  {item.loading && <span className="weather-spinner weather-spinner--small" />}
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mwh-tabs" role="tablist" aria-label="Weather history metrics">
          {HISTORY_TABS.map((metric) => (
            <button
              key={metric.key}
              type="button"
              className={`mwh-tab ${metric.key === activeMetric.key ? 'mwh-tab--active' : ''}`}
              onClick={() => {
                setActiveMetricKey(metric.key);
                setTooltip(null);
              }}
              role="tab"
              aria-selected={metric.key === activeMetric.key}
            >
              <HistoryMetricIcon name={metric.icon} />
              <span className="mwh-tab__label">{metric.label}</span>
            </button>
          ))}
        </div>

        {loading && !chartData.length ? (
          <div className="weather-loading">
            <span className="weather-spinner" />
          </div>
        ) : error ? (
          <div className="weather-empty">{error}</div>
        ) : chartData.length ? (
          <div className="mwh-chart-shell" onMouseLeave={clearTooltip}>
            {loading && (
              <div className="mwh-chart-loading-overlay" aria-hidden="true">
                <span className="weather-spinner weather-spinner--small" />
                <span>Updating graph...</span>
              </div>
            )}
            <svg
              className="mwh-chart"
              viewBox={`0 0 ${viewBoxWidth} ${VIEWBOX_HEIGHT}`}
              role="img"
              aria-label={`${selectedWindowDetails.label} ${activeMetric.label} weather history`}
            >
              {axisTicks.map((tick) => {
                const yRatio = yMax === yMin ? 0.5 : (tick - yMin) / (yMax - yMin);
                const y = CHART_PADDING.top + ((1 - clamp(yRatio, 0, 1)) * plotHeight);

                return (
                  <g key={tick}>
                    <line
                      className="mwh-grid-line"
                      x1={chartPaddingLeft}
                      x2={viewBoxWidth - CHART_PADDING.right}
                      y1={y}
                      y2={y}
                    />
                    <text
                      className="mwh-axis-label mwh-axis-label--y"
                      x={chartPaddingLeft - 12}
                      y={y + 4}
                    >
                      {formatAxisTick(tick, axisTickDecimals)}
                    </text>
                  </g>
                );
              })}

              <line
                className="mwh-axis-line"
                x1={chartPaddingLeft}
                x2={viewBoxWidth - CHART_PADDING.right}
                y1={VIEWBOX_HEIGHT - CHART_PADDING.bottom}
                y2={VIEWBOX_HEIGHT - CHART_PADDING.bottom}
              />

              {xTicks.map((point, index) => (
                <g key={`${point.observedAtMs}-${point.label}`}>
                  <line
                    className="mwh-axis-tick"
                    x1={point.x}
                    x2={point.x}
                    y1={VIEWBOX_HEIGHT - CHART_PADDING.bottom}
                    y2={VIEWBOX_HEIGHT - CHART_PADDING.bottom + 6}
                  />
                  <text
                    className="mwh-axis-label mwh-axis-label--x"
                    x={point.x}
                    y={VIEWBOX_HEIGHT - CHART_PADDING.bottom + 22}
                    textAnchor={
                      index === 0 ? 'start' : index === xTicks.length - 1 ? 'end' : 'middle'
                    }
                  >
                    {point.label}
                  </text>
                </g>
              ))}

              {isRainMetric ? (
                points.map((point) => {
                  if (typeof point.value !== 'number' || point.value <= 0) {
                    return null;
                  }

                  const barHeight = Math.max(
                    1,
                    (VIEWBOX_HEIGHT - CHART_PADDING.bottom) - point.y,
                  );

                  return (
                    <g key={point.observedAtMs}>
                      <rect
                        className="mwh-bar"
                        x={point.bucketWidth !== null ? point.x : point.x - (barWidth / 2)}
                        y={point.y}
                        width={point.bucketWidth !== null ? Math.max(0.5, point.bucketWidth * 0.9) : barWidth}
                        height={barHeight}
                        rx="8"
                      >
                        <title>{point.observedAtFullLabel}</title>
                      </rect>
                    </g>
                  );
                })
              ) : (
                <>
                  {activeMetric.key === 'temperature' && historyMeta?.mode === 'aggregate' && points.map((point) => (
                    point.rangeMinY !== null && point.rangeMaxY !== null && point.bucketWidth > 0 ? (
                      <rect
                        key={`${point.observedAtMs}-range`}
                        className="mwh-range-band"
                        x={point.x}
                        y={point.rangeMaxY}
                        width={point.bucketWidth}
                        height={Math.max(1, point.rangeMinY - point.rangeMaxY)}
                        fill={activeMetric.color}
                      />
                    ) : null
                  ))}
                  {lineSegments.map((segment) => (
                    <path
                      key={segment.key}
                      className="mwh-line"
                      d={segment.path}
                      style={{ stroke: segment.color }}
                    />
                  ))}
                  {points.filter((point) => typeof point.value === 'number').map((point) => (
                    <circle key={`${point.observedAtMs}-reading`} cx={point.x} cy={point.y} r={points.length < 20 ? 2.5 : 1} fill={getMetricPointColor(activeMetric, point.value)} />
                  ))}
                  {activeMetric.key === 'wind_speed' && points.map((point, index) => (
                    <WindDirectionMarker
                      key={`${point.observedAtMs}-wind`}
                      point={point}
                      index={index}
                      markerStep={windMarkerStep}
                      totalPoints={points.length}
                    />
                  ))}
                </>
              )}

              {activeTooltipPoint && (
                <>
                  <line
                    className="mwh-hover-guide"
                    x1={activeTooltipPoint.x}
                    x2={activeTooltipPoint.x}
                    y1={CHART_PADDING.top}
                    y2={VIEWBOX_HEIGHT - CHART_PADDING.bottom}
                  />
                  {!isRainMetric && typeof activeTooltipPoint.value === 'number' && (
                    <circle
                      className="mwh-hover-point"
                      cx={activeTooltipPoint.x}
                      cy={activeTooltipPoint.y}
                      r="5"
                      style={{ fill: getMetricPointColor(activeMetric, activeTooltipPoint.value) }}
                    />
                  )}
                </>
              )}

              {zoomSelectionRect && (
                <rect
                  className="mwh-zoom-selection"
                  x={zoomSelectionRect.x}
                  y={zoomSelectionRect.y}
                  width={zoomSelectionRect.width}
                  height={zoomSelectionRect.height}
                />
              )}

              <rect
                className="mwh-hit-area"
                x={chartPaddingLeft}
                y={CHART_PADDING.top}
                width={plotWidth}
                height={plotHeight}
                onMouseMove={handleChartMouseMove}
                onMouseLeave={clearTooltip}
                onPointerDown={startZoomSelection}
                onPointerMove={updateZoomSelection}
                onPointerUp={finishPointerZoomSelection}
                onPointerCancel={cancelZoomSelection}
              />
            </svg>

            <WeatherChartTooltip
              tooltip={tooltip}
              metric={activeMetric}
              temperatureUnit={temperatureUnit}
              windSpeedUnit={windSpeedUnit}
              precipitationUnit={precipitationUnit}
              pressureUnit={pressureUnit}
            />
          </div>
        ) : (
          <div className="weather-empty">No history points were returned for the selected window.</div>
        )}
      </div>
    </section>
  );
}

export default memo(MeshtasticWeatherHistoryChart);
